import type {
  ChatMessage,
  Event,
  ModelEvent,
} from "@tsmono/inspect-common/types";

/** Cached state for cross-call reuse. Hand the same object back via
 *  React's `useRef` (or `{ current: null }` in tests) on every call. */
export type MessagesFromEventsState = {
  messages: ChatMessage[];
  positions: Map<string, number>;
  events: readonly Event[];
  lastResult: ChatMessage[];
};

const isSuccessfulModelEvent = (e: Event): e is ModelEvent =>
  e.event === "model" && !e.error;

const createState = (): MessagesFromEventsState => ({
  messages: [],
  positions: new Map(),
  events: [],
  lastResult: [],
});

const insertAt = (
  state: MessagesFromEventsState,
  index: number,
  m: ChatMessage
): void => {
  state.messages.splice(index, 0, m);
  // Reindex everything from `index` onward; the splice shifted all
  // following messages right by one slot.
  for (let i = index; i < state.messages.length; i++) {
    const id = state.messages[i]?.id;
    if (id) state.positions.set(id, i);
  }
};

const processEvent = (state: MessagesFromEventsState, e: ModelEvent): void => {
  // Build the conversation list by merging each model event's `input`
  // (the model's view at that call) in order. Per event: reset a cursor
  // to the last index in the list, then walk its input. Known ids
  // re-anchor the cursor to their position without re-inserting; new
  // ids splice at cursor + 1. The output appends at the end of the
  // list (not at cursor + 1) and dedupes by id.
  //
  // The end-of-list cursor default lets an event whose input begins
  // with unseen messages (e.g. a compaction summary introduced ahead
  // of any prior anchor) extend the prefix rather than prepend. The
  // output-at-end rule then keeps the post-compaction assistant after
  // the summary, not between it and earlier history.
  let cursor = state.messages.length - 1;
  const seenInEvent = new Set<string>();
  for (const m of e.input) {
    if (!m.id || seenInEvent.has(m.id)) continue;
    seenInEvent.add(m.id);
    const known = state.positions.get(m.id);
    if (known !== undefined) {
      cursor = known;
    } else {
      cursor += 1;
      insertAt(state, cursor, m);
    }
  }
  const out = e.output.choices[0]?.message;
  if (out?.id && !state.positions.has(out.id)) {
    state.positions.set(out.id, state.messages.length);
    state.messages.push(out);
  }
};

/**
 * Reconstruct the conversation message list shown in the running-sample
 * Messages tab from a stream of events.
 *
 * If `stateRef` is supplied, the function tries to reuse cached work
 * from the previous call. When `events` is a pure prefix-extension of
 * the prior snapshot (events[i] === prior.events[i] for all prior
 * indices), only the new tail is processed; otherwise we recompute
 * from scratch. The cache is updated in place via `stateRef.current`.
 *
 * Note: the polling pipeline replaces the trailing event in place when
 * a streaming model call produces successive output chunks. That shows
 * up as divergence at the last index and forces a rebuild every poll
 * for the active turn, so the cache mainly earns its keep between
 * turns and on idle polls, not while a single model call is streaming.
 */
export const messagesFromEvents = (
  events: Event[],
  stateRef?: { current: MessagesFromEventsState | null }
): ChatMessage[] => {
  const prior = stateRef?.current ?? null;

  // Find first divergence with prior's events snapshot.
  let startFrom = 0;
  if (prior) {
    while (
      startFrom < prior.events.length &&
      startFrom < events.length &&
      prior.events[startFrom] === events[startFrom]
    ) {
      startFrom++;
    }
  }

  // Pure extension when we matched all of prior's events and `events`
  // is at least as long. Anything else (replacement, shrink, mid-array
  // reference change) triggers a full rebuild.
  const canReuse =
    prior !== null &&
    startFrom === prior.events.length &&
    events.length >= prior.events.length;

  let state: MessagesFromEventsState;
  if (canReuse) {
    state = prior;
  } else {
    state = createState();
    startFrom = 0;
  }

  // Track length to decide whether to refresh `lastResult`. processEvent
  // only ever grows `state.messages`, so an unchanged length means no
  // user-visible change and the prior cached array stays valid.
  const priorLen = state.messages.length;
  for (let i = startFrom; i < events.length; i++) {
    const e = events[i];
    if (e !== undefined && isSuccessfulModelEvent(e)) processEvent(state, e);
  }

  state.events = events;
  if (!canReuse || state.messages.length !== priorLen) {
    state.lastResult = state.messages.slice();
  }

  if (stateRef) stateRef.current = state;
  return state.lastResult;
};
